import { inject, Injectable } from '@angular/core';
import { ChangeTyped, Query, SearchResult } from '@damofire/data-commons';
import { Observable } from 'rxjs';
import { Asset, Column, System2d, Upright } from '../asset/asset.dto';
import { IdbAssetService } from '../asset/idb-asset.service';
import { IdbObservableAssetService } from '../asset/idb-asset-observable.service';
import { TypedDesQuery } from '../des.dto';
import { IdbIssueService } from '../issue/idb-issue.service';
import { IdbObservableIssueMediaService } from '../issue/idb-issue-media-observable.service';
import { IdbObservableIssueService } from '../issue/idb-issue-observable.service';
import { IdbIssueMedia, Issue } from '../issue/issue.dto';
import { IdbWarehouseLayoutTextService } from '../layout/idb-warehouse-layout-text.service';
import { IdbWarehouseOutlineService } from '../layout/idb-warehouse-outline.service';
import { IdbObservableNoteService } from '../note/idb-note-observable.service';
import { Note } from '../note/note.dto';
import { WarehouseStateSnapshotPayload } from '../snapshot/warehouse-state-snapshot.dto';
import { IdbSystemDrawingService } from '../system/idb-system.service';
import { IdbUprightService } from '../upright/idb-upright.service';
import { IdbWarehouseService } from '../warehouse/idb-warehouse.service';
import { LayoutTextSegment, WarehouseOutlineSegment } from '../warehouse/warehouse.dto';
import { HistoricalViewState } from './historical-view-state';
import { fromHistory, readFromHistory, reloadLayoutFromHistory, snapshotReads } from './history-aware.reads';

/**
 * Offline-build counterparts of the history-aware wrappers: same reads, same snapshot slices
 * (history-aware.reads), but subclassing the Idb implementations so the current state keeps coming
 * from IndexedDB.
 *
 * While a snapshot is selected, the overridden reads never reach IndexedDB, and HistoryAwareIdbIssueService
 * stops remote images from being copied to the device — historical media loads on demand from the
 * bucket. Returning to the current state is a flip back to the untouched Idb reads, with no resync.
 *
 * Registered only in offline builds and only behind the warehouseStateSnapshots flag.
 */

@Injectable()
export class HistoryAwareIdbIssueService extends IdbIssueService {
  private readonly history = inject(HistoricalViewState);

  override search(divisionId: string, query: TypedDesQuery): Promise<SearchResult<Issue>> {
    return readFromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.issueSearch(payload, query),
      () => super.search(divisionId, query),
    );
  }

  override get(divisionId: string, id: string): Promise<Issue> {
    return readFromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.issue(payload, id),
      () => super.get(divisionId, id),
    );
  }

  // Historical images must not be duplicated locally, nor their media written into the live store.
  protected override shouldPersistMedia(): boolean {
    return !this.history.isActive();
  }
}

@Injectable()
export class HistoryAwareIdbObservableIssueService extends IdbObservableIssueService {
  private readonly history = inject(HistoricalViewState);

  protected override observeOpen(divisionId: string, section?: string): Observable<ChangeTyped<Issue>[]> {
    return fromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.openIssues(payload, section),
      () => super.observeOpen(divisionId, section),
    );
  }

  protected override observeByInspection(divisionId: string, inspectionId: string): Observable<ChangeTyped<Issue>[]> {
    return fromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.inspectionIssues(payload, inspectionId),
      () => super.observeByInspection(divisionId, inspectionId),
    );
  }

  override search(divisionId: string, sQuery: TypedDesQuery, forceNoteInspectionId?: string): Observable<SearchResult<Issue>> {
    return fromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.issueSearch(payload, sQuery),
      () => super.search(divisionId, sQuery, forceNoteInspectionId),
    );
  }
}

@Injectable()
export class HistoryAwareIdbAssetService extends IdbAssetService {
  private readonly history = inject(HistoricalViewState);

  override get(division: string, id: string | number): Promise<Asset> {
    return readFromHistory(
      this.history,
      division,
      (payload) => snapshotReads.asset(payload, id),
      () => super.get(division, id),
    );
  }

  // Also covers the base class's getPreviousAsset(), which is built on search().
  override search(divisionId: string, query: Query): Promise<SearchResult<Asset>> {
    return readFromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.assetSearch(payload, query),
      () => super.search(divisionId, query),
    );
  }
}

@Injectable()
export class HistoryAwareIdbUprightService extends IdbUprightService {
  private readonly history = inject(HistoricalViewState);

  override get(division: string, id: string): Promise<Upright> {
    return readFromHistory(
      this.history,
      division,
      (payload) => snapshotReads.upright(payload, id),
      () => super.get(division, id),
    );
  }

  override list(division: string, systemId: string): Promise<Upright[]> {
    return readFromHistory(
      this.history,
      division,
      (payload) => snapshotReads.uprights(payload, systemId),
      () => super.list(division, systemId),
    );
  }

  override listColumns(division: string, assetIds: string[], rowIndex?: number): Promise<Column[]> {
    return readFromHistory(
      this.history,
      division,
      (payload) => snapshotReads.columns(payload, assetIds, rowIndex),
      () => super.listColumns(division, assetIds, rowIndex),
    );
  }
}

@Injectable()
export class HistoryAwareIdbSystemDrawingService extends IdbSystemDrawingService {
  private readonly history = inject(HistoricalViewState);
  private loadedGeneration: number = null;

  override get(division: string, system: string): Promise<System2d> {
    return readFromHistory(
      this.history,
      division,
      (payload) => snapshotReads.system(payload, system),
      () => super.get(division, system),
    );
  }

  /** Same cache-key fix as HistoryAwareSystemDrawingService.list. */
  override async list(division: string, section: string | null = null): Promise<System2d[]> {
    if (this.loadedGeneration !== this.history.generation) {
      this.loadedGeneration = this.history.generation;
      this.divisionId.set(division);
      this.section.set(section);
      await this.reloadSystems();

      return this.systems();
    }

    return super.list(division, section);
  }

  override reloadSystems(): Promise<void> {
    return readFromHistory(
      this.history,
      this.divisionId(),
      (payload) => this.systems.set(snapshotReads.systems(payload, this.section())),
      () => super.reloadSystems(),
    );
  }
}

@Injectable()
export class HistoryAwareIdbWarehouseOutlineService extends IdbWarehouseOutlineService {
  private readonly history = inject(HistoricalViewState);

  override search(divisionId: string): Promise<SearchResult<WarehouseOutlineSegment>> {
    return readFromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.outlineSearch(payload),
      () => super.search(divisionId),
    );
  }
}

@Injectable()
export class HistoryAwareIdbWarehouseLayoutTextService extends IdbWarehouseLayoutTextService {
  private readonly history = inject(HistoricalViewState);

  override search(divisionId: string): Promise<SearchResult<LayoutTextSegment>> {
    return readFromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.layoutTextSearch(payload),
      () => super.search(divisionId),
    );
  }
}

@Injectable()
export class HistoryAwareIdbWarehouseService extends IdbWarehouseService {
  private readonly history = inject(HistoricalViewState);
  private loadedGeneration: number = null;

  /** Same cache-key fix as HistoryAwareWarehouseService. */
  override async resolveOutline(divisionId: string): Promise<WarehouseOutlineSegment[]> {
    this.invalidateOnSourceChange();

    return super.resolveOutline(divisionId);
  }

  override async resolveLayoutTexts(divisionId: string): Promise<LayoutTextSegment[]> {
    this.invalidateOnSourceChange();

    return super.resolveLayoutTexts(divisionId);
  }

  override reloadOutlines(): Promise<void> {
    return reloadLayoutFromHistory(
      this.history,
      this.divisionId,
      (payload: WarehouseStateSnapshotPayload) => (this.outlines = snapshotReads.outline(payload)),
      () => super.reloadOutlines(),
      () => this.reloadOutlines(),
    );
  }

  override reloadLayoutTexts(): Promise<void> {
    return reloadLayoutFromHistory(
      this.history,
      this.divisionId,
      (payload: WarehouseStateSnapshotPayload) => (this.texts = snapshotReads.layoutTexts(payload)),
      () => super.reloadLayoutTexts(),
      () => this.reloadLayoutTexts(),
    );
  }

  private invalidateOnSourceChange(): void {
    if (this.loadedGeneration !== this.history.generation) {
      this.loadedGeneration = this.history.generation;
      this.invalidateResolvedLayout();
    }
  }
}

@Injectable()
export class HistoryAwareIdbObservableAssetService extends IdbObservableAssetService {
  private readonly history = inject(HistoricalViewState);

  override observeById(divisionId: string, id: string): Observable<ChangeTyped<Asset>> {
    return fromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.assetObservation(payload, id),
      () => super.observeById(divisionId, id),
    );
  }

  override search(divisionId: string, sQuery: TypedDesQuery, forceNoteInspectionId?: string): Observable<SearchResult<Asset>> {
    return fromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.assetSearch(payload, sQuery),
      () => super.search(divisionId, sQuery, forceNoteInspectionId),
    );
  }
}

@Injectable()
export class HistoryAwareIdbObservableNoteService extends IdbObservableNoteService {
  private readonly history = inject(HistoricalViewState);

  override observeDivisionNotes(divisionId: string): Observable<Note[]> {
    return fromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.divisionNotes(payload),
      () => super.observeDivisionNotes(divisionId),
    );
  }

  override observeNotes(divisionId: string, inspectionId: string, assetId?: string): Observable<Note[]> {
    return fromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.notes(payload, inspectionId, assetId),
      () => super.observeNotes(divisionId, inspectionId, assetId),
    );
  }

  override observeIssueNotes(divisionId: string, issueId: string): Observable<Note[]> {
    return fromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.issueNotes(payload, issueId),
      () => super.observeIssueNotes(divisionId, issueId),
    );
  }
}

@Injectable()
export class HistoryAwareIdbObservableIssueMediaService extends IdbObservableIssueMediaService {
  private readonly history = inject(HistoricalViewState);

  override observeMediaByIssue(division: string, issueId: string): Observable<ChangeTyped<IdbIssueMedia>[]> {
    return fromHistory(
      this.history,
      division,
      (payload) => snapshotReads.issueMedia<IdbIssueMedia>(payload, issueId),
      () => super.observeMediaByIssue(division, issueId),
    );
  }
}
